import { useEffect, useState } from 'react';
import {
    useParams,
    useNavigate,
    matchPath,
    useLocation
} from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
    useGetConfigCompetitions,
    useGetLanguages,
    useGetRoutes,
    useGetDisciplines,
    useGetEvents,
    useGetEventUnits,
    useGetNocs,
    useGetParticipantNames,
    useGetFunctionDisciplines,
    useGetFunctionEvents,
    useGetFunctionEventUnits,
    useGetFunctionRoutes,
    useGetFunctionNocs,
    useGetFunctionParticipantNames
} from "helpers/commonCodes";

import { ALL_URL, dateToday } from "./utils";
import { getEvents, getEventUnits } from 'data/Querys';
import { completeRsc, getEventRSC, getLongEventRSC } from 'helpers/rsc';
import { useGetRawParamsFromPath } from 'helpers/urlRoot';

function useGetEventsAndUnitsData() {
    const queryClient = useQueryClient();
    const [data, setData] = useState(null);

    return function (mlanguage, mcompetition, mdiscipline) {
        const fetchData = async () => {
            const result = await Promise.all([
                queryClient.fetchQuery({
                    queryKey: ['CC_Events', mcompetition, mdiscipline, mlanguage?.toUpperCase()],
                    queryFn: getEvents,
                    refetchInterval: 0,
                    staleTime: Infinity,
                }),
                queryClient.fetchQuery({
                    queryKey: ['CC_EventUnits', mcompetition, mdiscipline, mlanguage?.toUpperCase()],
                    queryFn: getEventUnits,
                    refetchInterval: 0,
                    staleTime: Infinity,
                })
            ]);

            setData(result);
        };

        fetchData();
        return data;
    }
}

export function useUrlFriendlyToParameters() {

    //Get values friendly from URL 
    let {
        mirs_path
        , mirs_competition
        , mirs_rsc
        , mirs_language
        , mirs_discipline
        , mirs_event_rsc
        , mirs_event
        , mirs_date
        , mirs_code
        , mirs_noc
        , mirs_medal
        , mirs_gender
    } = useParams()

    let rawParams = useGetRawParamsFromPath();

    //Get Lenguages from memory 
    const dataLanguage = useGetLanguages()
    let language = dataLanguage?.languages?.find(item => item.langIsoCode === mirs_language?.toUpperCase())?.lang || mirs_language?.toUpperCase();

    //Gets Competitions
    const dataCompetitions = useGetConfigCompetitions()
    let competition = dataCompetitions?.competitions?.find(item => item.path === mirs_competition)?.competitionCode || mirs_competition

    //routes
    const dataRoutes = useGetRoutes(language);
    if (mirs_path === undefined) mirs_path = rawParams.mirs_path

    let path = dataRoutes?.routes?.find(item => item.description === mirs_path)?.code || mirs_path

    //Get Disciplines from Memory 
    const dataDisciplines = useGetDisciplines({ mirs_competition: competition, mirs_language: language })
    let discipline = dataDisciplines?.disciplines?.find(item => item.seodescription === mirs_discipline && item.scheduled)?.code || mirs_discipline


    //Get Events from memory 
    const dataEvents = useGetEvents({ mirs_competition: competition, mirs_discipline: discipline, mirs_language: language })

    const garEquWorkarround = (item) => {
        return discipline === 'GAR' || discipline === 'EQU' || item.order > 0
    }
    let event = dataEvents?.events?.find(item => garEquWorkarround(item) && item.seodescription === mirs_event)?.code || mirs_event
    let eventRsc = dataEvents?.events?.find(item => garEquWorkarround(item) && item.seodescription === mirs_event_rsc)?.code || mirs_event_rsc
    let eventRsc2 = dataEvents?.events?.find(item => garEquWorkarround(item) && item.seodescription === mirs_event)?.code || mirs_event

    //Get Event Units from memory 
    const dataCC = useGetEventUnits({ mirs_competition: competition, mirs_discipline: discipline, mirs_language: language })

    //duplicates phase and unit get units 
    let startWith = (eventRsc) ? getEventRSC(eventRsc) : event
    let unit = dataCC?.eventUnits?.find(item => item.seodescription === mirs_rsc && item.code.startsWith(startWith) && item.code !== getLongEventRSC(event))?.code || mirs_rsc
    if (path === 'results' && /CLB.SPEED-------------/.test(event) && unit === '------------') {
        unit = completeRsc(event)
    }
    // nocs
    const dataNocs = useGetNocs({ mirs_competition: competition, mirs_language: language })
    let noc = dataNocs?.nocs?.find(item => item.seodescription === mirs_noc && item?.note === 'P')?.code || mirs_noc

    const dataParticipants = useGetParticipantNames({ mirs_competition: competition, mirs_language: language })
    let code = mirs_code

    if (path === 'bios'
        || path === 'coach'
        || path === 'official') {
        code = dataParticipants?.persons.find(item => item.seoname === mirs_code)?.code || mirs_code
    } else if (path === 'horse') {
        code = dataParticipants?.horses.find(item => item.seoname === mirs_code)?.code || mirs_code
    } else if (path === 'team') {
        code = dataParticipants?.teams.find(item => item.seoname === mirs_code)?.code || mirs_code
    }

    return {
        mirs_path: path
        , mirs_competition: competition
        , mirs_discipline: discipline
        , mirs_event_rsc: eventRsc
        , mirs_event: event
        , mirs_rsc: unit
        , mirs_language: language
        , mirs_date: mirs_date
        , mirs_code: code
        , mirs_noc: noc
        , mirs_medal
        , mirs_gender
        , mirs_event_rsc2: eventRsc2
    }
}

export function useGetParametersToUrlFriendly() {

    //Get data Descriptions from Memory 
    const dataLanguage = useGetLanguages();
    const dataCompetitions = useGetConfigCompetitions();
    const getDisciplines = useGetFunctionDisciplines();
    const getEvents = useGetFunctionEvents();
    const getEventUnits = useGetFunctionEventUnits();
    const getRoutes = useGetFunctionRoutes();
    const getNocs = useGetFunctionNocs();
    const getParticipants = useGetFunctionParticipantNames();
    const getGetEventsAndUnitsData = useGetEventsAndUnitsData();

    return function (paramsURL, path) {
        //Parameters from URL 
        const {
            mirs_path
            , mirs_competition
            , mirs_discipline
            , mirs_event_rsc
            , mirs_event
            , mirs_rsc
            , mirs_language
            , mirs_gender
            , mirs_date
            , mirs_code
            , mirs_noc
            , mirs_medal
        } = paramsURL
        const dataDisciplines = getDisciplines(paramsURL)
        const dataRoutes = getRoutes(paramsURL.mirs_language);
        const dataNocs = getNocs(paramsURL);
        const dataParticipants = getParticipants(paramsURL);
        let dataEvents = getEvents(paramsURL);
        let dataEventUnit = getEventUnits(paramsURL);

        //workarround to avoid hundreds calls to getGetEventsAndUnitsData function
        const avoidInfiniteCalls = (
            mirs_path === 'bios'
            || mirs_path === "team"
            || mirs_path === "coach"
            || mirs_path === 'schedulebynoc'
        )

        // if we have not loaded events and event units and have and event and event_unit in params
        if (avoidInfiniteCalls
            && (mirs_event !== undefined && dataEvents === undefined)
            && (mirs_rsc !== undefined && dataEventUnit === undefined)
        ) {

            const event_discipline = mirs_event ? mirs_event?.substring(0, 3) : mirs_rsc?.substring(0, 3);
            //console.log('CALL getGetEventsAndUnitsData')
            getGetEventsAndUnitsData(paramsURL.mirs_language, paramsURL.mirs_competition, event_discipline)
        }

        let language = dataLanguage?.languages?.find(item => item.lang === mirs_language?.toUpperCase())?.langIsoCode?.toLowerCase() || mirs_language;

        let competition = dataCompetitions?.competitions?.find(item => item.competitionCode === mirs_competition)?.path || mirs_competition;

        let discipline = dataDisciplines?.disciplines?.find(item => item.code === mirs_discipline)?.seodescription || mirs_discipline

        const garEquWorkarround = (item) => {

            return /GAR.---+/.test(item?.code) || /EQU......-+/.test(item?.code) || item.order > 0
        }
        let event = dataEvents?.events?.find(item => garEquWorkarround(item) && item.code === getEventRSC(mirs_event))?.seodescription || mirs_event;

        let eventRsc = dataEvents?.events?.find(item => item.order > 0 && item.code === getEventRSC(mirs_event_rsc))?.seodescription || mirs_event_rsc;

        let unit = dataEventUnit?.eventUnits?.find(item => item.code === mirs_rsc)?.seodescription || mirs_rsc

        let mpath = dataRoutes?.routes?.find(item => item.code === path)?.description || path

        let noc = dataNocs?.nocs?.find(item => item.code === mirs_noc)?.seodescription || mirs_noc

        let code = mirs_code

        if (path === 'bios'
            || path === 'coach'
            || path === 'official') {
            code = dataParticipants?.persons?.find(item => item.code === mirs_code)?.seoname || mirs_code
        } else if (path === 'horse') {
            code = dataParticipants?.horses?.find(item => item.code === mirs_code)?.seoname || mirs_code
        } else if (path === 'team') {
            code = dataParticipants?.teams?.find(item => item.code === mirs_code)?.seoname || mirs_code
        }

        return {
            mirs_path: mpath
            , mirs_competition: competition
            , mirs_discipline: discipline
            , mirs_event_rsc: eventRsc
            , mirs_event: event
            , mirs_rsc: unit
            , mirs_language: language
            , mirs_gender
            , mirs_date
            , mirs_code: code
            , mirs_noc: noc
            , mirs_medal
        }
    }
}

/**
 * Return String URL composed 
 *
 * @paramsURL :  get an object of key/value pairs of the dynamic params from the current
 * URL that were matched by the route path.
 * @path : path URL
 * 
 * Structure: language/path/discipline/date/rsc 
 */
export function useGetURL() {

    const cleanParam = function (isFriendly, paramFriendly, param) {
        let out = isFriendly ? paramFriendly : param
        return (out !== undefined && out !== '') ? `${out}` : '';
    }
    const rawParams = useGetRawParamsFromPath()
    const getRoutes = useGetFunctionRoutes()
    const getParametersToUrlFriendly = useGetParametersToUrlFriendly()

    return function (paramsURL, path) {
        var isFriendly = (rawParams?.mirs_language?.length === 2)
        const {
            mirs_path
            , mirs_language
            , mirs_competition
            , mirs_discipline
            , mirs_event
            , mirs_event_rsc
            , mirs_rsc
            , mirs_date
            , mirs_code
            , mirs_noc
            , mirs_medal
            , mirs_gender
        } = getParametersToUrlFriendly(paramsURL, path)
        const dataRoutes = getRoutes(paramsURL.mirs_language)
        let pathFriendly = isFriendly ? dataRoutes?.routes?.find(item => item.code === path)?.description || path : path

        // Get Parameters 
        let competition = isFriendly ? mirs_competition : paramsURL.mirs_competition;
        let discipline = isFriendly ? mirs_discipline : paramsURL.mirs_discipline;
        let language = isFriendly ? mirs_language : paramsURL.mirs_language;

        let date = cleanParam(isFriendly, mirs_date, paramsURL.mirs_date);
        let event = cleanParam(isFriendly, mirs_event, paramsURL.mirs_event);
        let eventRsc = cleanParam(isFriendly, mirs_event_rsc, paramsURL.mirs_event_rsc);
        let rsc = cleanParam(isFriendly, mirs_rsc, paramsURL.mirs_rsc);
        let code = cleanParam(isFriendly, mirs_code, paramsURL.mirs_code);
        let noc = cleanParam(isFriendly, mirs_noc, paramsURL.mirs_noc);
        let medal = cleanParam(isFriendly, mirs_medal, paramsURL.mirs_medal);
        let gender = cleanParam(isFriendly, mirs_gender, paramsURL.mirs_gender);

        let url = "/" + language + "/" + competition + "/" + pathFriendly
        if (pathFriendly === path) {

        }

        if (path === 'competitionstatus' || path === 'hearingschedule') {
            url += "/" + discipline
        }

        if (path === 'competitionschedule') {
            //N/A
        }

        if (path === 'disciplines') {
            //N/A
        }

        if (path === 'records') {
            if (discipline) {
                url += "/" + discipline;
                if (event)
                    url += "/" + event;
            }
        }

        if (path === 'schedule') {
            if (event) {
                (discipline) ? url += "/" + discipline : url += "/" + ALL_URL.ALL_DISC;
                (date) ? url += "/" + date : url += "/" + ALL_URL.ALL_DAYS;
                url += "/" + event
            } else if (date) {
                (discipline) ? url += "/" + discipline : url += "/" + ALL_URL.ALL_DISC;
                url += "/" + date
            } else if (discipline) {
                url += "/" + discipline
            }
        }

        if (path === 'schedulebynoc') {
            url += "/" + noc;
            if (event) {
                (discipline) ? url += "/" + discipline : url += "/" + ALL_URL.ALL_DISC;
                (date) ? url += "/" + date : url += "/" + ALL_URL.ALL_DAYS;
                url += "/" + event
            } else if (date) {
                (discipline) ? url += "/" + discipline : url += "/" + ALL_URL.ALL_DISC;
                url += "/" + date
            } else if (discipline) {
                url += "/" + discipline
            }
        }

        if (path === 'athletesbynoc') {
            url += "/" + noc;
            if (discipline) url += "/" + discipline
        }

        if (path === 'entriesbynoc' || path === 'medallistsbynoc' || path === 'profilebynoc') {
            url += "/" + noc
        }

        if (path === 'nocentriesbyevent') {
            url += "/" + discipline + "/" + noc
        }


        if (path === 'results') {
            if (mirs_event && mirs_event === paramsURL.mirs_event) {
                console.log('useGetURL - Warning, event not translated: ' + mirs_event)
            }
            if (mirs_rsc && mirs_rsc === paramsURL.mirs_rsc) {
                console.log('useGetURL - Warning, unit not translated: ' + mirs_rsc)
            }
            url += "/" + discipline + "/" + event + "/" + rsc
        }

        if (
            path === 'pool'
            || path === 'bracket'
            || path === 'ranking'
        ) {
            url += "/" + discipline + "/" + event
        }

        if (path === 'bios') {
            url += "/" + code
        }
        if (path === 'coach') {
            url += "/" + code
        }
        if (path === 'horse') {
            url += "/" + code
        }
        if (path === 'official') {
            url += "/" + code
        }
        if (path === 'team') {
            url += "/" + code
        }

        if (path === 'liveIndividual'
            || path === 'liveTeam'
            || path === 'placings') {
            url += "/" + discipline
        }

        if (path === 'medal') {
            if (discipline) {
                url += "/" + discipline
            }
        }

        if (path === 'multimedallists') {
            if (discipline) {
                url += "/" + discipline
            }
        }

        if (path === 'medallists') {
            if (date) {
                (discipline) ? url += "/" + discipline : url += "/" + ALL_URL.ALL_DISC;
                (event) ? url += "/" + event : url += "/" + ALL_URL.ALL_EVENT;
                url += "/" + date
            } else if (discipline) {
                url += "/" + discipline;
                if (event)
                    url += "/" + event;
            }
        }

        if (path === 'athletes') {
            if (noc) {
                (discipline) ? url += "/" + discipline : url += "/" + ALL_URL.ALL_DISC;
                url += "/" + noc
            } else if (discipline) {
                url += "/" + discipline
            }
        }

        if (path === 'teams' || path === 'coaches') {
            if (event) {
                (discipline) ? url += "/" + discipline : url += "/" + ALL_URL.ALL_DISC;
                (noc) ? url += "/" + noc : url += "/" + ALL_URL.ALL_NOC;
                url += "/" + event
            } else if (noc) {
                (discipline) ? url += "/" + discipline : url += "/" + ALL_URL.ALL_DISC;
                url += "/" + noc;
            } else if (discipline) {
                url += "/" + discipline;
            }
        }

        if (path === 'technicalofficials') {
            if (discipline) {
                url += "/" + discipline;
            }
        }

        if (path === 'horses') {
            if (noc) {
                url += "/" + noc
            }
        }

        if (path === 'entries') {
            if (event) {
                (discipline) ? url += "/" + discipline : url += "/" + ALL_URL.ALL_DISC;
                (noc) ? url += "/" + noc : url += "/" + ALL_URL.ALL_NOC;
                url += "/" + event
            } else if (noc) {
                (discipline) ? url += "/" + discipline : url += "/" + ALL_URL.ALL_DISC;
                url += "/" + noc
            } else if (discipline) {
                //url += "/" + discipline
                // url += "/" + discipline+"/"+ ALL_URL.ALL_NOC+"/"+ ALL_URL.ALL_EVENT;
                url += `/${discipline}/${ALL_URL.ALL_NOC}/${ALL_URL.ALL_EVENT}`
            }
        }

        if (path === 'entriesbydiscipline') {
            if (noc) {
                (discipline) ? url += "/" + discipline : url += "/" + ALL_URL.ALL_DISC;
                url += "/" + noc
            } else if (discipline) {
                url += "/" + discipline
            }
        }

        if (path === 'noclist') {
            // N/A
        }

        if (path === 'reports') {
            if (event) {
                (discipline) ? url += "/" + discipline : url += "/" + ALL_URL.ALL_DISC;
                url += "/" + event
            } else if (discipline) {
                url += "/" + discipline
            }
        }

        if (path === 'todaymatches' || path === 'todayathmatches' || path === 'todayteammatches') {
            if (discipline) {
                url += "/" + discipline
                const day = dateToday();
                if (day) { url += "/" + day }
            }
        }

        return (url)
    } // end return function
}

export const CanonicalLinkOld = () => {


    const dataLanguage = useGetLanguages()
    //const pathVars = window?.location?.href?.replace(/(\/)+/g, "$1")?.split('/')
    const location = useLocation();
    //evitar que falle en SSR ver como solucionar montaje de url
    if (typeof document === 'undefined') {
        return
    }

    const portSection = window.location.port && window.location.port !== '80' ? `:${window.location.port}` : '';
    const currentHost = `${window.location.protocol}//${window.location.hostname}${portSection}`;

    const pathVars = location.pathname?.replace(/(\/)+/g, "$1")?.split('/')
    const links = [...document.getElementsByTagName("link")]?.filter(tag => '|canonical|alternate|'.includes(tag?.rel));
    const header = document.getElementsByTagName("head")[0];

    let sortedLanguages = Array.isArray(dataLanguage?.languages) && [...dataLanguage?.languages]
    sortedLanguages = sortedLanguages?.filter(lan => lan?.lang === 'ENG')
    //const english = sortedLanguages?.find(lan => lan?.lang === 'ENG');
    //sortedLanguages = sortedLanguages?.filter(lan => lan?.lang !== 'ENG')
    //sortedLanguages?.sort((a, b) => a?.langIsoCode > b?.langIsoCode ? 1 : -1)
    //sortedLanguages.unshift(english)

    sortedLanguages?.forEach(lan => {
        const hreflang = lan?.langIsoCode?.toLowerCase();
        const rel = lan?.langIsoCode?.toUpperCase() === 'EN' ? 'canonical' : 'alternate'
        let link = links?.find(item => item?.hreflang === hreflang)
        //TODO calculate urls for friendly parameters

        const url = pathVars.reduce((url, current, index) => {
            const isLang = index === 1 && current?.length === 3
            const isIsoLang = index === 1 && current?.length === 2
            return url + (isLang ? lan?.lang : isIsoLang ? hreflang : current) + '/'
        }, '')?.slice(0, -1)

        if (!link && hreflang) {
            link = document.createElement("link");
            link.rel = rel
            link.hreflang = hreflang
            link.href = currentHost + url
            header.appendChild(link, header)
        } else if (link) {
            link.href = currentHost + url
        }
    })
}

export const CanonicalLink = () => {

    const location = useLocation();

    //evitar que falle en SSR ver como solucionar montaje de url
    if (typeof document === 'undefined') {
        return
    }

    const portSection = window.location.port && window.location.port !== '80' ? `:${window.location.port}` : '';
    const currentHost = `${window.location.protocol}//${window.location.hostname}${portSection}`;

    //const pathVars = location.pathname?.replace(/(\/)+/g, "$1")?.split('/')
    const links = [...document.getElementsByTagName("link")]?.filter(tag => '|canonical|alternate|'.includes(tag?.rel));
    const header = document.getElementsByTagName("head")[0];

    //const langInUrl = Array.isArray(pathVars) && pathVars.length > 0 ? pathVars[1] : 'en'
    //const isoLang =  dataLanguage?.languages?.find(item => item?.lang === langInUrl)?.langIsoCode.toLowerCase() || langInUrl?.toLowerCase();

    let link = links?.find(item => item?.rel === 'canonical')

    if (!link) {
        link = document.createElement("link")
        header.appendChild(link, header)
    }
    
    link.rel = 'canonical'
    if (currentHost?.includes('mntmir-dwrsmnt')){
        link.href = 'false_url_substring' + location.pathname
    } else {        
        link.href = currentHost + location.pathname
    }

}

export const useNavigateTo = (paramsURL, shouldNavigate, setShouldNavigate, path, state) => {
    //Parametros from URL no manipulated
    const navigate = useNavigate()
    const dataLanguage = useGetLanguages()
    const getUrl = useGetURL()
    let url = getUrl(paramsURL, path)
    let language = dataLanguage?.languages?.find(item => item.lang === paramsURL.mirs_language?.toUpperCase())?.lang
    language = language?.toUpperCase();
    const { mirs_rsc, mirs_event, mirs_discipline } = paramsURL
    const rsc = mirs_rsc ? mirs_rsc : mirs_event ? mirs_event : mirs_discipline ? mirs_discipline : ''

    useEffect(() => {
        if (shouldNavigate) {
            sessionStorage.setItem('RSC', rsc);
            navigate(url, state)
            setShouldNavigate(false)
        }
    }, [url, shouldNavigate, path])
};

export const useNavigateTo2 = (url, shouldNavigate, setShouldNavigate) => {


    const navigate = useNavigate()
    useEffect(() => {
        if (shouldNavigate) {
            navigate(url)
            setShouldNavigate(false)
        }
    }, [shouldNavigate])
};

export function useGetPath() {
    const getRouter = useGetFunctionRoutes();
    const params = useGetRawParamsFromPath();
    const location = useLocation()

    function getPath(path, code, dataRoutes) {
        return (path === code) ? path : dataRoutes?.routes?.find(item => item.code === code)?.description
    }

    return function (language) {
        const dataRoutes = getRouter(language)
        let mpath = params.mirs_path
        let pathUrl = location.pathname


        //translate urlfriendly or path 
        let pathSchedule = getPath(mpath, 'schedule', dataRoutes)
        let pathResult = getPath(mpath, 'results', dataRoutes)
        let pathPool = getPath(mpath, 'pool', dataRoutes)
        let pathBracket = getPath(mpath, 'bracket', dataRoutes)
        let pathRanking = getPath(mpath, 'ranking', dataRoutes)
        let pathMedal = getPath(mpath, 'medal', dataRoutes)
        let pathMedallists = getPath(mpath, 'medallists', dataRoutes)
        let pathMultiMedallists = getPath(mpath, 'multimedallists', dataRoutes)
        let pathCompetitionStatus = getPath(mpath, 'competitionstatus', dataRoutes)
        let pathHearingSchedule = getPath(mpath, 'hearingschedule', dataRoutes)
        let pathCompetitionschedule = getPath(mpath, 'competitionschedule', dataRoutes)
        let pathCompetitionscheduleByDay = getPath(mpath, 'schedulebyday', dataRoutes)
        let pathRecords = getPath(mpath, 'records', dataRoutes)
        let pathDisciplineRecords = getPath(mpath, 'disciplinerecords', dataRoutes)

        let pathBio = getPath(mpath, 'bios', dataRoutes)
        let pathTeam = getPath(mpath, 'team', dataRoutes)
        let pathCoach = getPath(mpath, 'coach', dataRoutes)
        let pathHorse = getPath(mpath, 'horse', dataRoutes)
        let pathOfficial = getPath(mpath, 'official', dataRoutes)

        let pathLiveIndividual = getPath(mpath, 'liveIndividual', dataRoutes)
        let pathLiveTeam = getPath(mpath, 'liveTeam', dataRoutes)
        let pathAthletes = getPath(mpath, 'athletes', dataRoutes)
        let pathTodayMatches = getPath(mpath, 'todaymatches', dataRoutes)
        let pathTodayAthMatches = getPath(mpath, 'todayathmatches', dataRoutes)
        let pathTodayTeamMatches = getPath(mpath, 'todayteammatches', dataRoutes)
        let pathPlacing = getPath(mpath, 'placings', dataRoutes)
        let pathReports = getPath(mpath, 'reports', dataRoutes)
        let pathTeams = getPath(mpath, 'teams', dataRoutes)
        let pathCoaches = getPath(mpath, 'coaches', dataRoutes)
        let pathTechnicalOfficials = getPath(mpath, 'technicalofficials', dataRoutes)
        let pathHorses = getPath(mpath, 'horses', dataRoutes)
        let pathEntries = getPath(mpath, 'entries', dataRoutes)
        let pathEntriesDiscipline = getPath(mpath, 'entriesbydiscipline', dataRoutes)
        let pathNocList = getPath(mpath, 'noclist', dataRoutes)

        let pathScheduleByNoc = getPath(mpath, 'schedulebynoc', dataRoutes)
        let pathEntriesByNoc = getPath(mpath, 'entriesbynoc', dataRoutes)
        let pathMedallistsByNoc = getPath(mpath, 'medallistsbynoc', dataRoutes)
        let pathProfileByNoc = getPath(mpath, 'profilebynoc', dataRoutes)
        let pathAthletesByNoc = getPath(mpath, 'athletesbynoc', dataRoutes)
        let pathNocEntriesByEvent = getPath(mpath, 'nocentriesbyevent', dataRoutes)


        //check by path or pathfriendly 
        let isSchedule = matchPath({ path: `/:mirs_language/:mirs_competition/${pathSchedule}/*` }, pathUrl);
        let isResult = matchPath({ path: `/:mirs_language/:mirs_competition/${pathResult}/*` }, pathUrl);
        let isTodayMatches = matchPath({ path: `/:mirs_language/:mirs_competition/${pathTodayMatches}/*` }, pathUrl);
        let isTodayAthMatches = matchPath({ path: `/:mirs_language/:mirs_competition/${pathTodayAthMatches}/*` }, pathUrl);
        let isTodayTeamMatches = matchPath({ path: `/:mirs_language/:mirs_competition/${pathTodayTeamMatches}/*` }, pathUrl);
        let isPool = matchPath({ path: `/:mirs_language/:mirs_competition/${pathPool}/*` }, pathUrl);
        let isBracket = matchPath({ path: `/:mirs_language/:mirs_competition/${pathBracket}/*` }, pathUrl);
        let isRanking = matchPath({ path: `/:mirs_language/:mirs_competition/${pathRanking}/*` }, pathUrl);
        let isCompetitionStatus = matchPath({ path: `/:mirs_language/:mirs_competition/${pathCompetitionStatus}/*` }, pathUrl);
        let isHearingSchedule = matchPath({ path: `/:mirs_language/:mirs_competition/${pathHearingSchedule}/*` }, pathUrl);
        let isMedal = matchPath({ path: `/:mirs_language/:mirs_competition/${pathMedal}/*` }, pathUrl);
        let isMedallists = matchPath({ path: `/:mirs_language/:mirs_competition/${pathMedallists}/*` }, pathUrl);
        let isMultiMedallists = matchPath({ path: `/:mirs_language/:mirs_competition/${pathMultiMedallists}/*` }, pathUrl);
        let isAthletes = matchPath({ path: `/:mirs_language/:mirs_competition/${pathAthletes}/*` }, pathUrl);
        let isTeams = matchPath({ path: `/:mirs_language/:mirs_competition/${pathTeams}/*` }, pathUrl);
        let isCoaches = matchPath({ path: `/:mirs_language/:mirs_competition/${pathCoaches}/*` }, pathUrl);
        let isTechnicalOfficials = matchPath({ path: `/:mirs_language/:mirs_competition/${pathTechnicalOfficials}/*` }, pathUrl);
        let isHorses = matchPath({ path: `/:mirs_language/:mirs_competition/${pathHorses}/*` }, pathUrl);
        let isEntriesDiscipline = matchPath({ path: `/:mirs_language/:mirs_competition/${pathEntriesDiscipline}/*` }, pathUrl);
        let isEntries = matchPath({ path: `/:mirs_language/:mirs_competition/${pathEntries}/*` }, pathUrl);
        let isCompetitionSchedule = matchPath({ path: `/:mirs_language/:mirs_competition/${pathCompetitionschedule}/*` }, pathUrl);
        let isCompetitionScheduleByDay = matchPath({ path: `/:mirs_language/:mirs_competition/${pathCompetitionscheduleByDay}/*` }, pathUrl);
        let isNocList = matchPath({ path: `/:mirs_language/:mirs_competition/${pathNocList}/*` }, pathUrl);
        let isRecords = matchPath({ path: `/:mirs_language/:mirs_competition/${pathRecords}/*` }, pathUrl);
        let isScheduleByNoc = matchPath({ path: `/:mirs_language/:mirs_competition/${pathScheduleByNoc}/*` }, pathUrl);
        let isEntriesByNoc = matchPath({ path: `/:mirs_language/:mirs_competition/${pathEntriesByNoc}/*` }, pathUrl);
        let isMedallistsByNoc = matchPath({ path: `/:mirs_language/:mirs_competition/${pathMedallistsByNoc}/*` }, pathUrl);
        let isProfileByNoc = matchPath({ path: `/:mirs_language/:mirs_competition/${pathProfileByNoc}/*` }, pathUrl);
        let isAthletesByNoc = matchPath({ path: `/:mirs_language/:mirs_competition/${pathAthletesByNoc}/*` }, pathUrl);
        let isNocEntriesByEvent = matchPath({ path: `/:mirs_language/:mirs_competition/${pathNocEntriesByEvent}/*` }, pathUrl);
        let isReports = matchPath({ path: `/:mirs_language/:mirs_competition/${pathReports}/*` }, pathUrl);


        let isBios = matchPath({ path: `/:mirs_language/:mirs_competition/${pathBio}/*` }, pathUrl);
        let isTeam = matchPath({ path: `/:mirs_language/:mirs_competition/${pathTeam}/*` }, pathUrl);
        let isCoach = matchPath({ path: `/:mirs_language/:mirs_competition/${pathCoach}/*` }, pathUrl);
        let isHorse = matchPath({ path: `/:mirs_language/:mirs_competition/${pathHorse}/*` }, pathUrl);
        let isOfficial = matchPath({ path: `/:mirs_language/:mirs_competition/${pathOfficial}/*` }, pathUrl);

        return (
            {
                pathFriendly: {
                    pathSchedule: pathSchedule,
                    pathResult: pathResult,
                    pathPool: pathPool,
                    pathBracket: pathBracket,
                    pathRanking: pathRanking,
                    pathCompetitionStatus: pathCompetitionStatus,
                    pathHearingSchedule: pathHearingSchedule,
                    pathMedal: pathMedal,
                    pathMedallists: pathMedallists,
                    pathMultiMedallists: pathMultiMedallists,
                    pathScheduleByNoc: pathScheduleByNoc,
                    pathCompetitionschedule: pathCompetitionschedule,
                    pathCompetitionscheduleByDay: pathCompetitionscheduleByDay,
                    pathRecords: pathRecords,
                    pathDisciplineRecords: pathDisciplineRecords,
                    pathBio: pathBio,
                    pathTeam: pathTeam,
                    pathCoach: pathCoach,
                    pathHorse: pathHorse,
                    pathOfficial: pathOfficial,
                    pathLiveIndividual: pathLiveIndividual,
                    pathLiveTeam: pathLiveTeam,
                    pathAthletes: pathAthletes,
                    pathTodayMatches: pathTodayMatches,
                    pathTodayAthMatches: pathTodayAthMatches,
                    pathTodayTeamMatches: pathTodayTeamMatches,
                    pathNocList: pathNocList,
                    pathPlacing: pathPlacing,
                    pathReports: pathReports,
                    pathTeams: pathTeams,
                    pathCoaches: pathCoaches,
                    pathTechnicalOfficials: pathTechnicalOfficials,
                    pathHorses: pathHorses,
                    pathEntries: pathEntries,
                    pathEntriesDiscipline: pathEntriesDiscipline,
                    pathEntriesByNoc: pathEntriesByNoc,
                    pathMedallistsByNoc: pathMedallistsByNoc,
                    pathProfileByNoc: pathProfileByNoc,
                    pathAthletesByNoc: pathAthletesByNoc,
                    pathNocEntriesByEvent: pathNocEntriesByEvent
                },

                url: {
                    isResult: isResult,
                    isPool: isPool,
                    isBracket: isBracket,
                    isRanking: isRanking,
                    isSchedule: isSchedule,
                    isCompetitionStatus: isCompetitionStatus,
                    isHearingSchedule: isHearingSchedule,
                    isMedal: isMedal,
                    isMedallists: isMedallists,
                    isMultiMedallists: isMultiMedallists,
                    isTeams: isTeams,
                    isCoaches: isCoaches,
                    isHorses: isHorses,
                    isEntries: isEntries,
                    isEntriesDiscipline: isEntriesDiscipline,
                    isEntriesByNoc: isEntriesByNoc,
                    isCompetitionSchedule: isCompetitionSchedule,
                    isCompetitionScheduleByDay: isCompetitionScheduleByDay,
                    isAthletes: isAthletes,
                    isTechnicalOfficials: isTechnicalOfficials,
                    isNocList: isNocList,
                    isRecords: isRecords,
                    isMedallistsByNoc: isMedallistsByNoc,
                    isProfileByNoc: isProfileByNoc,
                    isAthletesByNoc: isAthletesByNoc,
                    isScheduleByNoc: isScheduleByNoc,
                    isBios: isBios,
                    isTeam: isTeam,
                    isCoach: isCoach,
                    isHorse: isHorse,
                    isOfficial: isOfficial,
                    isTodayMatches: isTodayMatches,
                    isTodayAthMatches: isTodayAthMatches,
                    isTodayTeamMatches: isTodayTeamMatches,
                    isNocEntriesByEvent: isNocEntriesByEvent,
                    isReports: isReports
                }

            }
        );
    }
}

export function useGetRealPath() {
    const obj = useGetPath();
    return function (language) {
        const objUrl = obj(language);
        if (objUrl.url.isResult) return ("results")
        else if (objUrl.url.isPool) return ("pool")
        else if (objUrl.url.isBracket) return ("bracket")
        else if (objUrl.url.isRanking) return ("ranking")
        else if (objUrl.url.isSchedule) return ("schedule")
        else if (objUrl.url.isCompetitionStatus) return ("competitionstatus")
        else if (objUrl.url.isHearingSchedule) return ("hearingschedule")
        else if (objUrl.url.isMedal) return ("medal")
        else if (objUrl.url.isMedallists) return ("medallists")
        else if (objUrl.url.isMultiMedallists) return ("multimedallists")
        else if (objUrl.url.isCompetitionSchedule) return ("competitionschedule")
        else if (objUrl.url.isCompetitionScheduleByDay) return ("schedulebyday")
        else if (objUrl.url.isAthletes) return ("athletes")
        else if (objUrl.url.isTeams) return ("teams")
        else if (objUrl.url.isCoaches) return ("coaches")
        else if (objUrl.url.isTechnicalOfficials) return ("technicalofficials")
        else if (objUrl.url.isHorses) return ("horses")
        else if (objUrl.url.isNocList) return ("noclist")
        else if (objUrl.url.isRecords) return ("records")
        else if (objUrl.url.isEntries) return ("entries")
        else if (objUrl.url.isEntriesDiscipline) return ("entriesbydiscipline")
        else if (objUrl.url.isEntriesByNoc) return ("entriesbynoc")
        else if (objUrl.url.isMedallistsByNoc) return ("medallistsbynoc")
        else if (objUrl.url.isProfileByNoc) return ("profilebynoc")
        else if (objUrl.url.isAthletesByNoc) return ("athletesbynoc")
        else if (objUrl.url.isScheduleByNoc) return ("schedulebynoc")
        else if (objUrl.url.isTodayMatches) return ("todaymatches")
        else if (objUrl.url.isTodayAthMatches) return ("todayathmatches")
        else if (objUrl.url.isTodayTeamMatches) return ("todayteammatches")
        else if (objUrl.url.isNocEntriesByEvent) return ("nocentriesbyevent")
        else if (objUrl.url.isReports) return ("reports")

    }
}
